/**
 * Block 7 (2026-05-25): Commission API client.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';


export type CommissionDirection = 'RECEIVED' | 'GIVEN';
export type CommissionRateType = 'PERCENTAGE' | 'FIXED_PER_UNIT';
export type CommissionVatHandler = 'COMMITENT' | 'COMMISSIONER';
export type CommissionReportPeriod = 'WEEKLY' | 'MONTHLY' | 'PER_BATCH';
export type SettlementPaymentStatus = 'PENDING' | 'PARTIAL' | 'SETTLED';


export interface CommissionContract {
  id: string;
  organization_id: string;
  contract_number: string;
  contract_date: string;
  valid_from: string;
  valid_until: string | null;
  direction: CommissionDirection;
  counterparty_id: string;
  commission_rate_type: CommissionRateType;
  commission_rate_value: string;
  vat_handler: CommissionVatHandler;
  report_period: CommissionReportPeriod;
  is_active: boolean;
  deletion_mark: boolean;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommissionContractCreate {
  organization_id: string;
  contract_number: string;
  contract_date: string;
  valid_from: string;
  valid_until?: string | null;
  direction: CommissionDirection;
  counterparty_id: string;
  commission_rate_type?: CommissionRateType;
  commission_rate_value: string;
  vat_handler?: CommissionVatHandler;
  report_period?: CommissionReportPeriod;
  is_active?: boolean;
  comment?: string | null;
}


export interface TransferItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  unit_cost: string;
  total_cost?: string;
}

export interface CommissionGoodsTransfer {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  commission_contract_id: string;
  responsible_employee_id: string | null;
  transfer_act_number: string | null;
  external_source?: string | null;
  items: TransferItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3b:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface CommissionGoodsTransferCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  commission_contract_id: string;
  responsible_employee_id?: string | null;
  transfer_act_number?: string | null;
  comment?: string | null;
  items: Array<{
    line_number: number;
    nomenclature_id: string;
    quantity: string;
    unit_cost: string;
  }>;
}


export interface SaleItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  unit_sale_price: string;
  line_sale_amount?: string;
  line_commission_amount: string;
  line_vat_amount: string;
  unit_cost_snapshot: string;
}

export interface CommissionSale {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  commission_contract_id: string;
  final_buyer_id: string | null;
  final_buyer_name: string | null;
  total_sale_amount: string;
  total_commission_amount: string;
  total_vat_amount: string;
  external_source?: string | null;
  items: SaleItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3b:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface CommissionSaleCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  commission_contract_id: string;
  final_buyer_id?: string | null;
  final_buyer_name?: string | null;
  total_vat_amount?: string;
  comment?: string | null;
  items: Array<{
    line_number: number;
    nomenclature_id: string;
    quantity: string;
    unit_sale_price: string;
    line_commission_amount?: string;
    line_vat_amount?: string;
    unit_cost_snapshot?: string;
  }>;
}


export interface CommissionSettlementReport {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  commission_contract_id: string;
  period_year: number;
  period_month: number;
  total_sales_amount: string;
  total_commission_amount: string;
  total_vat_amount: string;
  total_to_remit: string;
  sales_count: number;
  payment_status: SettlementPaymentStatus;
  paid_amount: string;
  external_source?: string | null;
  created_at: string;
  updated_at: string;
  // Block 5.3b:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface BuildSettlementForPeriodRequest {
  organization_id: string;
  commission_contract_id: string;
  period_year: number;
  period_month: number;
}

export interface BuildSettlementForPeriodResponse {
  sales_count: number;
  total_sales_amount: string;
  total_commission_amount: string;
  total_vat_amount: string;
  total_to_remit: string;
  related_sale_ids: string[];
  warnings: string[];
}


const CC_BASE = '/refs/commission-contracts';
const T_BASE = '/documents/commission-goods-transfer';
const S_BASE = '/documents/commission-sale';
const SR_BASE = '/documents/commission-settlement-report';


export const commissionContract = {
  list: (params?: { organization_id?: string; direction?: CommissionDirection;
                    is_active?: boolean; limit?: number }) =>
    api.get<Page<CommissionContract>>(CC_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<CommissionContract>(`${CC_BASE}/${id}`).then((r) => r.data),
  create: (p: CommissionContractCreate) =>
    api.post<CommissionContract>(CC_BASE, p).then((r) => r.data),
};


export const commissionTransfer = {
  list: (params?: { organization_id?: string; commission_contract_id?: string;
                    posted?: boolean; limit?: number }) =>
    api.get<Page<CommissionGoodsTransfer>>(T_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<CommissionGoodsTransfer>(`${T_BASE}/${id}`).then((r) => r.data),
  create: (p: CommissionGoodsTransferCreate) =>
    api.post<CommissionGoodsTransfer>(T_BASE, p).then((r) => r.data),
  post: (id: string) =>
    api.post<CommissionGoodsTransfer>(`${T_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<CommissionGoodsTransfer>(`${T_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${T_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};


export const commissionSale = {
  list: (params?: { organization_id?: string; commission_contract_id?: string;
                    posted?: boolean; limit?: number }) =>
    api.get<Page<CommissionSale>>(S_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<CommissionSale>(`${S_BASE}/${id}`).then((r) => r.data),
  create: (p: CommissionSaleCreate) =>
    api.post<CommissionSale>(S_BASE, p).then((r) => r.data),
  post: (id: string) =>
    api.post<CommissionSale>(`${S_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<CommissionSale>(`${S_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${S_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};


export const commissionSettlement = {
  list: (params?: { organization_id?: string; commission_contract_id?: string;
                    posted?: boolean; limit?: number }) =>
    api.get<Page<CommissionSettlementReport>>(SR_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<CommissionSettlementReport>(`${SR_BASE}/${id}`).then((r) => r.data),
  post: (id: string) =>
    api.post<CommissionSettlementReport>(`${SR_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<CommissionSettlementReport>(`${SR_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${SR_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  buildForPeriod: (p: BuildSettlementForPeriodRequest) =>
    api.post<BuildSettlementForPeriodResponse>(`${SR_BASE}/build-for-period`, p)
      .then((r) => r.data),
};
